// src/remotion/autocutCapture.tsx
//
// AutoCut capture of a post layout (autoclipper-worker
// docs/superpowers/specs/2026-10-19-autocut-any-video-design.md, gap 2). AutoCut, the native
// renderer, draws the moving media of a PostFrame export itself and takes the static layout
// from Chrome: `PostFrameCapture` renders the layout once per pass, with every media slot
// replaced by an <AutocutSlot> placeholder.
//
// - `under`: the decoration under the media (slot cells keep their own background).
// - `over`: only what layouts mark with `data-autocut-over` (titles, badges, scrims).
// - `masks-<k>`: the shapes of slots 3k..3k+2, white in the R, G and B channels.
//
// Once fonts and layout settle, the root logs one `AUTOCUT_SLOTS <json>` line: each slot in
// paint order with its rect (composition px, transforms included), fit, blur, filler playlist
// and mask channel. The worker reads it with renderStill's onBrowserLog.
import React, { createContext, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { continueRender, delayRender } from 'remotion';

export type CapturePass = 'under' | 'over' | `masks-${number}`;

export const AUTOCUT_SLOTS_LOG_PREFIX = 'AUTOCUT_SLOTS ';
/** Slots per mask image: one per RGB channel. */
export const SLOTS_PER_MASK = 3;

export interface AutocutSlotMeta {
  kind: 'main' | 'copy' | 'filler';
  /** How the media fills the slot's rect (main clip and copies). */
  fit?: 'cover' | 'contain';
  muted?: boolean;
  /** Filler playlist. */
  urls?: string[];
  fits?: Record<string, string>;
  positions?: Record<string, string>;
  speed?: number;
}

export interface CapturedSlot extends AutocutSlotMeta {
  id: string;
  /** Composition px, after every transform (the box the media fills). */
  rect: { x: number; y: number; w: number; h: number };
  /** CSS blur on the slot or its ancestors, in composition px. */
  blurPx: number;
  /** Product of the slot's and its ancestors' CSS opacity. */
  opacity: number;
  /**
   * What AutoCut cannot draw on this slot's ancestors: a filter other than blur, a rotation or
   * skew (the rect is then only a bounding box), a blend mode. The importer warns about each.
   */
  unsupported: string[];
  /** `masks-<maskIndex>` holds this slot's shape in channel `maskChannel` (0 R, 1 G, 2 B). */
  maskIndex: number;
  maskChannel: number;
}

export interface CaptureReport {
  version: 1;
  width: number;
  height: number;
  /** `static_image`: the layout shows a still instead of the clip (the clip's audio still plays). */
  mainMode: 'clip' | 'static_image';
  masks: number;
  slots: CapturedSlot[];
}

interface CaptureContextValue {
  pass: CapturePass;
  register: (id: string, meta: AutocutSlotMeta | null) => void;
  /** Slot ids in paint (DOM) order, once measured. */
  order: string[] | null;
}

const CaptureContext = createContext<CaptureContextValue | null>(null);

/** Non-null while a PostFrameCapture renders: media components render <AutocutSlot> instead. */
export const useAutocutCapture = (): CaptureContextValue | null => useContext(CaptureContext);

const CHANNEL_COLORS = ['rgb(255,0,0)', 'rgb(0,255,0)', 'rgb(0,0,255)'];

const maskPassIndex = (pass: CapturePass): number | null =>
  pass.startsWith('masks-') ? Number(pass.slice('masks-'.length)) : null;

/** Stands in for a media slot (fills its container like the media would). */
export const AutocutSlot: React.FC<{ id: string; meta: AutocutSlotMeta }> = ({ id, meta }) => {
  const capture = useAutocutCapture();
  const { register } = capture ?? {};
  useLayoutEffect(() => {
    register?.(id, meta);
    return () => register?.(id, null);
  });
  const maskIndex = capture ? maskPassIndex(capture.pass) : null;
  const position = capture?.order?.indexOf(id) ?? -1;
  const inMask = maskIndex !== null && position >= 0 && Math.floor(position / SLOTS_PER_MASK) === maskIndex;
  return (
    <div
      data-autocut-slot={id}
      {...(inMask ? { 'data-autocut-mask': '' } : {})}
      style={{
        position: 'absolute',
        inset: 0,
        ...(inMask
          ? { backgroundColor: CHANNEL_COLORS[position % SLOTS_PER_MASK], mixBlendMode: 'plus-lighter' }
          : {}),
      }}
    />
  );
};

const PASS_CSS: Record<'under' | 'over' | 'masks', string> = {
  under: '[data-autocut-capture] [data-autocut-over] { visibility: hidden !important; }',
  over: [
    '[data-autocut-capture] * { visibility: hidden !important; }',
    '[data-autocut-capture] [data-autocut-over], [data-autocut-capture] [data-autocut-over] * { visibility: visible !important; }',
  ].join('\n'),
  masks: [
    '[data-autocut-capture] * { visibility: hidden !important; }',
    '[data-autocut-capture] [data-autocut-mask] { visibility: visible !important; }',
  ].join('\n'),
};

/** `matrix(a, b, c, d, …)` / `matrix3d(…)` with anything but scale and translation. */
const rotatesOrSkews = (transform: string): boolean => {
  if (/rotate|skew/.test(transform)) return true;
  const m = /^matrix(3d)?\(([^)]*)\)$/.exec(transform.trim());
  if (!m) return false;
  const v = m[2].split(',').map(Number);
  const off = m[1] ? [v[1], v[2], v[4], v[6], v[8], v[9]] : [v[1], v[2]];
  return off.some((x) => Math.abs(x) > 1e-6);
};

/** Blur, opacity and what AutoCut cannot draw, from the slot up to the capture root. */
const ancestry = (el: HTMLElement, root: HTMLElement) => {
  let blurPx = 0;
  let opacity = 1;
  const unsupported = new Set<string>();
  for (let node: HTMLElement | null = el; node && node !== root; node = node.parentElement) {
    const style = getComputedStyle(node);
    const filter = style.filter || 'none';
    if (filter !== 'none') {
      const match = /^blur\(([\d.]+)px\)$/.exec(filter.trim());
      if (match) {
        // Local px → composition px with the element's own on-screen scale.
        const scale = node.offsetWidth ? node.getBoundingClientRect().width / node.offsetWidth : 1;
        blurPx += Number(match[1]) * scale;
      } else {
        unsupported.add(`filter:${filter}`);
      }
    }
    const value = Number(style.opacity);
    if (Number.isFinite(value)) opacity *= value;
    if (style.transform && style.transform !== 'none' && rotatesOrSkews(style.transform)) unsupported.add('transform');
    if (style.mixBlendMode && style.mixBlendMode !== 'normal' && !node.hasAttribute('data-autocut-mask')) {
      unsupported.add(`blend:${style.mixBlendMode}`);
    }
  }
  return { blurPx, opacity, unsupported: [...unsupported] };
};

/**
 * Measures the slots rendered under `root`, in paint (DOM) order; slots never nest. Elements are
 * looked up now rather than kept from registration, so a remounted slot is never measured
 * detached.
 */
export const measureSlots = (root: HTMLElement, metas: Map<string, AutocutSlotMeta>): CapturedSlot[] => {
  const origin = root.getBoundingClientRect();
  return [...root.querySelectorAll<HTMLElement>('[data-autocut-slot]')]
    .map((el) => ({ el, id: el.dataset.autocutSlot ?? '' }))
    .filter(({ id }) => metas.has(id))
    .map(({ el, id }, index) => {
      const meta = metas.get(id)!;
      const r = el.getBoundingClientRect();
      return {
        id,
        ...meta,
        rect: { x: r.left - origin.left, y: r.top - origin.top, w: r.width, h: r.height },
        ...ancestry(el, root),
        maskIndex: Math.floor(index / SLOTS_PER_MASK),
        maskChannel: index % SLOTS_PER_MASK,
      };
    });
};

/**
 * Plain <img>s (avatars, logos, photos) carry no delayRender, so a single still can be taken
 * before they decode. Resolves once each image under `root` has decoded or failed (a failed
 * image is missing from PostFrame's export too).
 */
export const imagesSettled = (root: HTMLElement): Promise<void> =>
  Promise.all([...root.querySelectorAll('img')].map((img) => {
    if (typeof img.decode === 'function') return img.decode().catch(() => undefined);
    if (img.complete) return undefined;
    return new Promise((resolve) => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  })).then(() => undefined);

/**
 * The capture root: applies the pass, waits for fonts, images and layout, measures the slots,
 * logs the report once, then lets Remotion take the still.
 */
export const AutocutCaptureRoot: React.FC<{
  pass: CapturePass;
  width: number;
  height: number;
  mainMode: CaptureReport['mainMode'];
  children: React.ReactNode;
}> = ({ pass, width, height, mainMode, children }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const slots = useRef(new Map<string, AutocutSlotMeta>());
  const [order, setOrder] = useState<string[] | null>(null);
  const [handle] = useState(() => delayRender('AutoCut capture: slot layout'));

  const value = useMemo<CaptureContextValue>(() => ({
    pass,
    order,
    register: (id, meta) => {
      if (meta) slots.current.set(id, meta);
      else slots.current.delete(id);
    },
  }), [pass, order]);

  useLayoutEffect(() => {
    if (order) return;
    let cancelled = false;
    // Fonts and images move text-flow cells; measure after they load and two more frames of
    // layout.
    const nextFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));
    (document.fonts?.ready ?? Promise.resolve())
      .then(() => (rootRef.current ? imagesSettled(rootRef.current) : undefined))
      .then(nextFrame).then(nextFrame).then(() => {
      if (cancelled || !rootRef.current) return;
      const measured = measureSlots(rootRef.current, slots.current);
      const report: CaptureReport = {
        version: 1,
        width,
        height,
        mainMode,
        masks: Math.ceil(measured.length / SLOTS_PER_MASK),
        slots: measured,
      };
      console.log(AUTOCUT_SLOTS_LOG_PREFIX + JSON.stringify(report));
      setOrder(measured.map((s) => s.id));
    });
    return () => { cancelled = true; };
  }, [order, width, height, mainMode]);

  // The mask colors render with `order`; release the still after that commit.
  useLayoutEffect(() => {
    if (order) requestAnimationFrame(() => continueRender(handle));
  }, [order, handle]);

  const css = PASS_CSS[maskPassIndex(pass) !== null ? 'masks' : (pass as 'under' | 'over')];
  return (
    <CaptureContext.Provider value={value}>
      <div ref={rootRef} data-autocut-capture="" style={{ position: 'absolute', inset: 0 }}>
        <style>{css}</style>
        {children}
      </div>
    </CaptureContext.Provider>
  );
};
